import React, { useEffect, useState, useRef } from 'react'
import { keyframes } from '@mui/system'
import { Box, Fade, IconButton } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import jwtDecode from 'jwt-decode'
import axios from 'axios'
import config from '../config'

// Disco mode keyframes
const discoBackground = keyframes`
  0% { background: linear-gradient(45deg, #ff0080, #ff8c00, #40e0d0); }
  14% { background: linear-gradient(45deg, #ff8c00, #40e0d0, #9370db); }
  28% { background: linear-gradient(45deg, #40e0d0, #9370db, #00ff00); }
  42% { background: linear-gradient(45deg, #9370db, #00ff00, #ff1493); }
  57% { background: linear-gradient(45deg, #00ff00, #ff1493, #ffd700); }
  71% { background: linear-gradient(45deg, #ff1493, #ffd700, #00bfff); }
  85% { background: linear-gradient(45deg, #ffd700, #00bfff, #ff0080); }
  100% { background: linear-gradient(45deg, #00bfff, #ff0080, #ff8c00); }
`

const discoText = keyframes`
  0% { color: #ff0080; text-shadow: 0 0 10px #ff0080; }
  14% { color: #ff8c00; text-shadow: 0 0 10px #ff8c00; }
  28% { color: #40e0d0; text-shadow: 0 0 10px #40e0d0; }
  42% { color: #9370db; text-shadow: 0 0 10px #9370db; }
  57% { color: #00ff00; text-shadow: 0 0 10px #00ff00; }
  71% { color: #ff1493; text-shadow: 0 0 10px #ff1493; }
  85% { color: #ffd700; text-shadow: 0 0 10px #ffd700; }
  100% { color: #00bfff; text-shadow: 0 0 10px #00bfff; }
`

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`

const rotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`

const slideIn = keyframes`
  from {
    transform: translateY(-100%) scale(0);
    opacity: 0;
  }
  to {
    transform: translateY(0) scale(1);
    opacity: 1;
  }
`

// Meme URLs - you can add your own meme URLs here
const MEME_URLS = [
  'https://i.imgflip.com/30b1gx.jpg', // Success kid
  'https://i.imgflip.com/1bij.jpg', // Success kid original
  'https://i.imgflip.com/265k.jpg', // Imagination Spongebob
  'https://i.imgflip.com/1g8my4.jpg', // Distracted boyfriend
  'https://i.imgflip.com/9ehk.jpg', // Surprised Pikachu
  'https://i.imgflip.com/4t0m5.jpg', // Bernie Sanders
  'https://i.imgflip.com/26am.jpg', // Drake
  'https://i.imgflip.com/1ur9b0.jpg', // Roll Safe
]

const DiscoMode = ({ children }) => {
  const [isDiscoMode, setIsDiscoMode] = useState(false)
  const [showMeme, setShowMeme] = useState(false)
  const [showDistortion, setShowDistortion] = useState(false)
  const [currentMeme, setCurrentMeme] = useState('')
  const [userEmail, setUserEmail] = useState(null)
  const [youtubeAudioUrl, setYoutubeAudioUrl] = useState(null)
  const currentMemeIdRef = useRef(null)
  const audioRef = useRef(null)

  useEffect(() => {
    // Get user email from token for meme polling
    const token = localStorage.getItem('usertoken')
    console.log('🎉 DiscoMode: Initializing...')
    if (token) {
      try {
        const decodedToken = jwtDecode(token)
        const email = decodedToken.email
        console.log('🎉 DiscoMode: User email:', email)
        setUserEmail(email)
      } catch (error) {
        console.error('🎉 DiscoMode: Error decoding token:', error)
      }
    } else {
      console.log('🎉 DiscoMode: No token found')
    }
  }, [])

  useEffect(() => {
    if (!userEmail) return

    console.log('🎉 DiscoMode: Setting up meme polling for', userEmail)

    // Poll for memes from server every 5 seconds
    const pollForMemes = async () => {
      try {
        const token = localStorage.getItem('usertoken')
        if (!token || token === 'null' || token === 'undefined') return;

        const response = await axios.get(
          `${config.API_BASE_URL}/api/disco/get-memes/${encodeURIComponent(userEmail)}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        )

        if (response.data.memes && response.data.memes.length > 0) {
          const item = response.data.memes[0]
          console.log('🎉 DiscoMode: Received item from server!', item)

          if (currentMemeIdRef.current === item.id) {
            return
          }
          currentMemeIdRef.current = item.id

          if (item.type === 'distortion') {
            setShowDistortion(true)
            setTimeout(() => {
              setShowDistortion(false)
              if (currentMemeIdRef.current === item.id) currentMemeIdRef.current = null
            }, item.duration || 5000)
          } else {
            setCurrentMeme(item.url)
            setShowMeme(true)
            
            if (item.musicUrl) {
              const ytMatch = item.musicUrl.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/i)
              if (ytMatch && ytMatch[1]) {
                const videoId = ytMatch[1]
                setYoutubeAudioUrl(`https://www.youtube.com/embed/${videoId}?autoplay=1&controls=0&showinfo=0&autohide=1`)
              } else {
                if (audioRef.current) audioRef.current.pause()
                audioRef.current = new Audio(item.musicUrl)
                audioRef.current.volume = 1.0
                audioRef.current.play().catch(e => console.error('🎉 DiscoMode: Audio play failed', e))
              }
            }
            
            setTimeout(() => {
              setShowMeme(false)
              if (currentMemeIdRef.current === item.id) currentMemeIdRef.current = null
              setYoutubeAudioUrl(null)
              if (audioRef.current) {
                audioRef.current.pause()
                audioRef.current = null
              }
            }, item.duration || 5000)
          }
        }
      } catch (error) {
        console.error('🎉 DiscoMode: Error polling for memes:', error)
      }
    }

    // Poll every 5 seconds
    const pollInterval = setInterval(pollForMemes, 5000)

    // Also allow manual toggle with keyboard shortcut (Ctrl+Alt+D)
    const handleKeyPress = (e) => {
      if (e.ctrlKey && e.altKey && e.key === 'd') {
        e.preventDefault()
        console.log('🎉 DiscoMode: Manual toggle triggered!')
        setIsDiscoMode((prev) => !prev)
      }
    }

    window.addEventListener('keydown', handleKeyPress)
    return () => {
      clearInterval(pollInterval)
      window.removeEventListener('keydown', handleKeyPress)
    }
  }, [userEmail])

  // Memes are now controlled by server broadcasts only
  // No automatic random memes

  if (!userEmail) return <>{children}</>

  console.log('🎉 DiscoMode: Rendering component, isDiscoMode:', isDiscoMode)

  return (
    <>
      {/* App content */}
      {children}
      {/* Disco overlay */}
      {isDiscoMode && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            pointerEvents: 'none',
            zIndex: 9998,
            animation: `${discoBackground} 3s linear infinite`,
            opacity: 0.15,
            mixBlendMode: 'overlay',
          }}
        />
      )}

      {/* Disco mode indicator */}
      {isDiscoMode && (
        <Box
          sx={{
            position: 'fixed',
            top: 20,
            right: 20,
            zIndex: 9999,
            padding: '10px 20px',
            background: 'rgba(0, 0, 0, 0.8)',
            borderRadius: '20px',
            animation: `${pulse} 2s ease-in-out infinite`,
          }}
        >
          <Box
            component="span"
            sx={{
              fontSize: '18px',
              fontWeight: 'bold',
              animation: `${discoText} 3s linear infinite`,
            }}
          >
            🎉 DISCO MODE ACTIVATED 🎉
          </Box>
        </Box>
      )}

      {/* Rotating disco ball */}
      {isDiscoMode && (
        <Box
          sx={{
            position: 'fixed',
            top: 100,
            right: 20,
            zIndex: 9999,
            fontSize: '60px',
            animation: `${rotate} 3s linear infinite`,
            pointerEvents: 'none',
          }}
        >
          🪩
        </Box>
      )}

      {/* Meme popup */}
      <Fade in={showMeme} timeout={500}>
        <Box
          sx={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 10000,
            maxWidth: '90vw',
            maxHeight: '90vh',
            background: 'white',
            borderRadius: '20px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
            overflow: 'hidden',
            animation: `${slideIn} 0.5s ease-out`,
            border: '5px solid',
            borderImage: `linear-gradient(45deg, #ff0080, #ff8c00, #40e0d0, #9370db) 1`,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <IconButton
            onClick={() => setShowMeme(false)}
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              background: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              '&:hover': {
                background: 'rgba(0, 0, 0, 0.9)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
          <img
            src={currentMeme}
            alt="Random Meme"
            style={{
              maxWidth: '100%',
              maxHeight: '90vh',
              objectFit: 'contain',
              display: 'block',
            }}
          />
        </Box>
      </Fade>

      {/* Distortion (Broken Screen) Overlay */}
      {showDistortion && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 11000,
            pointerEvents: 'none',
            backgroundImage: 'url(/broken-screen.png?v=lcd_glitch)', // Cache bust for new asset
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            opacity: 0.9,
            mixBlendMode: 'normal', // Multiply might hide white cracks on dark bg, normal/screen is safer for general use or sticking to multiply if the image is white-on-transparent
          }}
        />
      )}
      
      {youtubeAudioUrl && (
        <iframe
          title="youtube-audio"
          src={youtubeAudioUrl}
          allow="autoplay"
          style={{ position: 'absolute', width: '1px', height: '1px', opacity: 0, pointerEvents: 'none' }}
        />
      )}
    </>
  )
}

export default DiscoMode
